import { RefObject, useEffect, useRef, useState } from 'react';

export type ScrollDirection = 'up' | 'down';

/**
 * Tracks the current scroll direction. Reports 'up' when at the top or
 * scrolling up, 'down' when scrolling down.
 *
 * By default listens on `document`, catching scrolls from any container.
 * Pass `targetRef` to track a specific scrollable element instead.
 */
export function useScrollDirection(
  threshold: number = 10,
  targetRef?: RefObject<HTMLElement>
): ScrollDirection {
  const [direction, setDirection] = useState<ScrollDirection>('up');
  const lastScrollY = useRef(0);

  useEffect(() => {
    const node: HTMLElement | Document = targetRef?.current ?? document;
    // Scroll events don't bubble, so the document default needs the capture
    // phase to catch scrolling inside nested `overflow-y-auto` containers
    // (the app shell doesn't scroll `window`). A specific element receives
    // its own scroll events directly, so no capture is needed there.
    const capture = node === document;

    // Baseline against the target's current position so the first delta
    // isn't measured from a stale value belonging to a previous target.
    lastScrollY.current =
      node instanceof HTMLElement ? node.scrollTop : window.scrollY;

    let ticking = false;

    const handleScroll = (event: Event) => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const target = targetRef?.current ?? event.target;
        const currentScrollY =
          target instanceof HTMLElement ? target.scrollTop : window.scrollY;
        const delta = currentScrollY - lastScrollY.current;

        if (currentScrollY <= 0) {
          setDirection('up');
        } else if (Math.abs(delta) > threshold) {
          setDirection(delta < 0 ? 'up' : 'down');
        }

        lastScrollY.current = currentScrollY;
        ticking = false;
      });
    };

    node.addEventListener('scroll', handleScroll, { capture, passive: true });
    return () => node.removeEventListener('scroll', handleScroll, { capture });
  }, [threshold, targetRef]);

  return direction;
}
